/**
 * Parent bridge v1: read-only events from the sportsbook to the page that embeds it
 * (a B2B partner's site, or one of our skins through sportIframe.vue).
 *
 * Every message is {type, v, payload}. The parent only listens: nothing in this
 * bridge lets it change the slip or place a bet. A message goes out only when the
 * sportsbook runs inside a frame.
 *
 * targetOrigin: the embedding page, when the skin lists its trusted parent sites
 * (parentOrigins.js); '*' when it lists none, like every other message the sportsbook
 * sends to its parent.
 */
import { parentTargetOrigins } from '@/services/parentBridge/parentOrigins';

export const PARENT_BRIDGE_VERSION = 1;

export const PARENT_EVENTS = Object.freeze({
    SLIP_CHANGED:        'SPORT_SLIP_CHANGED',
    BET_PLACED:          'SPORT_BET_PLACED',
    BET_REVIEW_PROPOSAL: 'SPORT_BET_REVIEW_PROPOSAL',
    SESSION_EXPIRED:     'SPORT_SESSION_EXPIRED',
    AUTH_FAILED:         'SPORT_AUTH_FAILED',
    CART_STATE_CHANGED:  'SPORT_CART_STATE_CHANGED',
});

/**
 * @param {string} type - one of PARENT_EVENTS
 * @param {object} [payload]
 * @returns {{type: string, v: number, payload: object}}
 */
export function buildParentMessage(type, payload) {
    return { type, v: PARENT_BRIDGE_VERSION, payload: payload || {} };
}

/**
 * Runs a piece of the bridge so that nothing in it reaches the caller. The bridge only
 * reports: the cart, the socket handlers, the store and the bootstrap go on as if it
 * was not there, whatever goes wrong while building or posting an event.
 *
 * @param {string} label - what failed, for the log
 * @param {function} fn
 */
export function runSafely(label, fn) {
    try {
        fn();
    } catch (err) {
        console.error(`[ParentBridge] ${label} failed:`, err);
    }
}

/**
 * Posts one event to the parent page.
 *
 * @param {string} type - one of PARENT_EVENTS
 * @param {object} [payload]
 * @param {Window} [win] - the sportsbook window (injected by the tests)
 * @returns {boolean} true when the message was posted
 */
export function postToParent(type, payload, win = typeof window !== 'undefined' ? window : undefined) {
    if (!win || !win.parent || win.parent === win) return false;
    try {
        const message = buildParentMessage(type, payload);
        const targets = parentTargetOrigins(win);
        targets.forEach(target => win.parent.postMessage(message, target));
        return targets.length > 0;
    } catch (err) {
        console.error(`[ParentBridge] ${type} not posted:`, err);
        return false;
    }
}
